import { experimentsService } from '@wix/cashier-common/dist/src/utils/experimentsService';
import cn from 'classnames';
import React from 'react';
import { useAdaptedComponents } from '../../../../hooks/useAdaptedComponents';
import { useSiteStyles } from '../../../../hooks/useSiteStyles';
import { Experiment } from '../../../../types/Experiment';
import { Theme } from '../../../../types/Theme';
import { DH } from '../../../../utils/DataHook';
import { t } from '../../../../utils/i18n';
import * as s from './styles.scss';
import { TextFieldProps } from './types';

interface TextFieldViewProps
  extends Omit<TextFieldProps, 'onChange' | 'onBlur'> {
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur(): void;
}

export const TextFieldView: React.FunctionComponent<TextFieldViewProps> = (
  props
) => {
  const {
    required,
    theme,
    value,
    name,
    label,
    deviceType = 'desktop',
    type = 'text',
    shouldApplySiteStyles,
    isValid,
    touched,
    errorMessage,
    inputRef,

    onBlur,
    onChange,
  } = props;

  const { textColor } = useSiteStyles();
  const isError: boolean = !isValid && touched;
  const AdaptedComponents = useAdaptedComponents();

  if (AdaptedComponents) {
    return (
      <AdaptedComponents.TextField
        required={required}
        theme={theme}
        value={value}
        name={name}
        label={t(label)}
        deviceType={deviceType}
        onChange={onChange}
        onBlur={onBlur}
        errorMessage={isError ? t(errorMessage) : null}
        type={type}
        inputRef={inputRef}
      />
    );
  }

  return (
    <div
      data-hook={DH.PaymentDetails.TextField.Container}
      className={cn({
        [s.root]: true,
        [s.modalTheme]: theme === Theme.modal,
        [s.desktop]: deviceType === 'desktop',
        [s.mobile]: deviceType === 'mobile',
        [s.invalid]: isError,
        [s.required]: required,
      })}
    >
      <label
        data-hook={DH.PaymentDetails.TextField.Label}
        style={shouldApplySiteStyles && textColor ? { color: textColor } : null}
        className={s.label}
        htmlFor={name}
      >
        {experimentsService.isExperimentEnabled(
          Experiment.AsteriskAfterText
        ) ? (
          <>
            {t(label)}
            {required && (
              <span
                data-hook={DH.PaymentDetails.TextField.Asterisk}
                className={s.required}
              >
                {' '}
                *
              </span>
            )}
          </>
        ) : (
          <>
            {required && (
              <span
                data-hook={DH.PaymentDetails.TextField.Asterisk}
                className={s.required}
              >
                *{' '}
              </span>
            )}
            {t(label)}
          </>
        )}
      </label>
      <input
        data-hook={DH.PaymentDetails.TextField.Input(name)}
        ref={inputRef}
        className={s.input}
        id={name}
        name={name}
        required={required}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        aria-invalid={isError && errorMessage ? 'true' : 'false'}
        aria-required={required}
        type={type}
      />
      {isError && errorMessage && (
        <label
          data-hook={DH.PaymentDetails.TextField.ErrorMessage(name)}
          className={s.errorLabel}
          htmlFor={name}
        >
          {t(errorMessage)}
        </label>
      )}
    </div>
  );
};
TextFieldView.displayName = 'TextFieldView';
